import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';

class ProductActivityIndicator extends HTMLElement {
  #timer;
  #index = 0;
  #section;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    if (this.dataset.context === 'product') this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#refresh();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise.then(({ detail }) => {
      if (!detail?.html) return;
      if (detail.newProduct) this.dataset.productId = detail.newProduct.id;
      else if (detail.productId && detail.productId !== this.dataset.productId) return;

      const selector = `product-activity-indicator[data-block-id="${CSS.escape(this.dataset.blockId)}"]`;
      const replacement = detail.html.querySelector(selector);
      if (!replacement) return;

      for (const target of ['available', 'low', 'out']) {
        const current = this.querySelector(`[data-dynamic][data-inventory-target="${target}"]`);
        const next = replacement.querySelector(`[data-dynamic][data-inventory-target="${target}"]`);
        if (current && next) morph(current, next);
      }
      this.dataset.inventoryState = replacement.dataset.inventoryState;
      this.dataset.trackedInventory = replacement.dataset.trackedInventory;
      this.#refresh();
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[product-activity] Variant update failed:', error);
    });
  };

  #refresh() {
    clearTimeout(this.#timer);
    this.#index = 0;
    const messages = [...this.querySelectorAll('[data-activity-message]')];
    const context = this.dataset.context;
    const mode = this.dataset.mode;
    const inventoryState = this.dataset.inventoryState;
    const rotation = this.dataset.rotation === 'true';
    const defaultAnimation = this.dataset.animation || 'none';

    for (const separator of this.querySelectorAll('.product-activity__separator:empty')) separator.textContent = this.dataset.separator || ' · ';
    for (const message of messages) {
      message.classList.remove('is-active');
      message.hidden = true;
      message.setAttribute('aria-hidden', 'true');
      const override = message.dataset.animation;
      message.dataset.resolvedAnimation = override && override !== 'inherit' ? override : defaultAnimation;
    }
    if (mode === 'hidden') return void (this.hidden = true);

    const manual = messages.filter((message) => {
      if (message.dataset.dynamic === 'true' || message.dataset.enabled !== 'true') return false;
      return context === 'card' ? message.dataset.cardVisible === 'true' : message.dataset.productVisible === 'true';
    });
    const dynamic = messages.filter((message) =>
      message.dataset.dynamic === 'true' &&
      message.dataset.enabled === 'true' &&
      message.dataset.inventoryTarget === inventoryState
    );
    let eligible = mode === 'dynamic' ? dynamic : mode === 'manual' ? manual : [...dynamic, ...manual];
    eligible.sort((a, b) => Number(b.dataset.priority || 0) - Number(a.dataset.priority || 0));

    if (!eligible.length) return void (this.hidden = true);
    this.hidden = false;
    const rotating = rotation ? eligible.filter((message) => message.dataset.rotate === 'true') : [eligible[0]];
    const visible = rotating.length ? rotating : [eligible[0]];
    for (const message of visible) message.hidden = false;
    this.#show(visible, 0);
    if (rotation && visible.length > 1) this.#schedule(visible);
  }

  #show(messages, index) {
    for (const [messageIndex, message] of messages.entries()) {
      const active = messageIndex === index;
      message.classList.toggle('is-active', active);
      message.setAttribute('aria-hidden', String(!active));
    }
    const summary = this.querySelector('[data-activity-summary]');
    if (summary) summary.textContent = messages[index]?.innerText?.trim() || '';
  }

  #schedule(messages) {
    const interval = Math.max(Number(this.dataset.interval) || 5000, 1000);
    this.#timer = setTimeout(() => {
      const next = this.#index + 1;
      if (next >= messages.length && this.dataset.loop !== 'true') return;
      this.#index = next % messages.length;
      this.#show(messages, this.#index);
      this.#schedule(messages);
    }, interval);
  }
}
if (!customElements.get('product-activity-indicator')) customElements.define('product-activity-indicator', ProductActivityIndicator);
